import { Card, Col, Form, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { convertQueryStringToObj, fileHelpers, objectHelpers } from '../../../helpers';
import { getDayJsObjectWithFormattedString } from '../../../helpers/date.helpers';
import _ from '../../../helpers/lodash';
import { displayErrorNotifications, displaySuccessNotification } from '../../../helpers/toast.helpers';
import PrimaryLayout from '../../../layouts/primary-layout';
import { useLoader } from '../../../stores/use-loader';
import { useBasicDetails } from '../../../stores/use-promotion';
import { priceListService } from '../services/pricelist.service';
import { IMasterCatalogListing, IPriceListListing } from '../types/price-list-types.s';
import PriceListDiscountForm from './price-list-discount-form';

interface IPriceListEdit {}

const PriceListEdit: React.FunctionComponent<IPriceListEdit> = () => {
  //new declare
  const { parentId } = useParams();
  const [priceListCreateForm] = Form.useForm();
  const [priceListBasicDetailsForm] = Form.useForm();
  const navigate = useNavigate();

  //new State
  const [referenceBlockVisiblity, setReferenceBlockVisiblity] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [paretListDropDown, setParentListDropDown] = React.useState<any>([]);
  const [masterCatalogOptions, setMasterCatalogOptions] = React.useState<any[]>([]);
  const [basicDetailsResp, setBasicDetailsResp] = React.useState({} as any);
  const [formVisiblityBasedOnStatus, setFormVisiblityBasedOnStatus] = React.useState<boolean>(false);
  const [refUpdatBlock, setRefUpdatBlock] = React.useState<boolean>(false);

  //other declerations
  const queryString = searchParams.toString();
  const queryStringObj = convertQueryStringToObj(queryString);
  const { offset, backTabTo } = queryStringObj;

  //zustand declare
  const { setLoading } = useLoader(({ loading, setLoading }) => ({ loading, setLoading }));
  const { productData, setProductData, masterOptions, setMasterOptions, parentOptions, setParentOptions } =
    useBasicDetails(
      ({ productData, setProductData, masterOptions, setMasterOptions, parentOptions, setParentOptions }) => ({
        productData,
        setProductData,
        masterOptions,
        setMasterOptions,
        parentOptions,
        setParentOptions
      })
    );

  React.useEffect(() => {
    loadIntialData();
  }, []);

  const loadIntialData = () => {
    setLoading(true);
    if (backTabTo === 'BASIC-DETAILS') {
      loadbasicDetailstab();
    } else {
      loadPriceEnteriestab();
    }
    setLoading(false);
  };

  const onTabChange = (tabName: any) => {
    setLoading(true);
    if (tabName === 'BASIC-DETAILS') {
      loadbasicDetailstab();
    } else {
      loadPriceEnteriestab();
    }
    setLoading(false);
  };

  const loadbasicDetailstab = async () => {
    priceListCreateForm.setFieldsValue({
      price_list_form_tab: 'BASIC-DETAILS'
    });
    loadTabData();
    getAllMasterCatalogData(0);
    getAllParentCatalogData(0);
  };

  const loadTabData = async () => {
    const { data, errors } = await priceListService.getSelectedItemPriceListData(parentId as string);
    if (_.isEmpty(errors)) {
      setBasicDetailsResp(data);
      setProductData(data);
      setDataToPriceListBasicDetailsForm(data);
      handleFormVisiblityChecks(data);
    } else {
      displayErrorNotifications(errors);
    }
  };

  const handleFormVisiblityChecks = (data: any) => {
    if (data.status === 'ACTIVE' || data.status === 'SCHEDULED') {
      setFormVisiblityBasedOnStatus(true);
    }
    setRefUpdatBlock(data?.ref_update_block);
  };

  const getAllMasterCatalogData = async (offset = 0) => {
    let params = { offset, limit: 100 };
    const filteredParams = objectHelpers.deleteUndefinedValuesFromObject(params);
    const { data, errors } = await priceListService.getMasterData(filteredParams);
    if (_.isEmpty(errors)) {
      let masterCatalogList = data?.data?.map((cv: IMasterCatalogListing) => ({ label: cv.name, value: cv.id }));
      setMasterCatalogOptions(masterCatalogList);
      setMasterOptions(masterCatalogList);
    } else {
      displayErrorNotifications(errors);
    }
  };

  const getAllParentCatalogData = async (offset = 0) => {
    const params = { offset, limit: 100 };
    const filteredParams = objectHelpers.deleteUndefinedValuesFromObject(params);
    const { data, errors } = await priceListService.getPriceList(filteredParams);
    if (_.isEmpty(errors)) {
      let parentCatalogList = data?.price_lists?.map((cv: IPriceListListing) => ({
        label: cv.price_list_name,
        value: cv.id
      }));
      setParentListDropDown(parentCatalogList);
      setParentOptions(parentCatalogList);
    } else {
      displayErrorNotifications(errors);
    }
  };

  const loadPriceEnteriestab = async () => {
    priceListCreateForm.setFieldsValue({
      price_list_form_tab: 'PRICE-ENTERIES'
    });
  };

  const setDataToPriceListBasicDetailsForm = (basicDetailsData: IPriceListListing) => {
    const {
      price_list_name,
      status,
      description,
      currency_code,
      start_date,
      end_date,
      master_catalog_id,
      rank,
      is_exclusive,
      is_resolvable,
      is_pos,
      pos,
      is_online,
      online,
      customer_restrictions,
      id,
      parent_price_list_id,
      start_time,
      end_time,
      timezone,
      rounding,
      catalog_type,
      is_active,
      ref_update_block,
      import_catalog_ref_name
    } = basicDetailsData;
    setReferenceBlockVisiblity(ref_update_block);
    const sd = getDayJsObjectWithFormattedString(start_date, 'YYYY-MM-DD');
    const ed = end_date ? getDayJsObjectWithFormattedString(end_date, 'YYYY-MM-DD') : '';
    const st = getDayJsObjectWithFormattedString(start_time, 'HH:mm:ss');
    const et = end_time ? getDayJsObjectWithFormattedString(end_time, 'HH:mm:ss') : '';
    let POS_Data = pos.map((cv: any) => cv.site_name);
    let ONLINE_Data = online.map((cv: any) => cv.site_name);
    const isCustomeRestrictionsArray = Array.isArray(customer_restrictions);
    priceListCreateForm.setFieldsValue({
      name: price_list_name,
      status: status,
      description: description,
      currency: currency_code,
      rank: rank,
      time_Zone: timezone,
      startDate: sd,
      endDate: ed,
      startTime: st,
      endTime: et,
      rounding_Policy: rounding?.rounding_policy,
      suggestion_list:
        catalog_type === 'MASTER-CATALOG'
          ? 'list_master'
          : catalog_type === 'PARENT-LIST'
          ? 'list_parent'
          : 'list_other',
      select_from_master_catalog_dropDown: master_catalog_id ? master_catalog_id : null,
      parent_price_list: parent_price_list_id ? parent_price_list_id : null,
      select_from_master_catalog_TextBox: import_catalog_ref_name ? import_catalog_ref_name : null,
      is_active: is_active ? 'active' : 'deactive',
      isPriceListExclusive: is_exclusive ? 'priceListExe_yes' : 'priceListExe_no',
      isPriceListResolvable: is_resolvable ? 'priceListRes_yes' : 'priceListRes_no',
      POS: is_pos,
      specific_pos: POS_Data,
      Ecommerce: is_online,
      specific_ecommerce: ONLINE_Data,
      customerRestriction: isCustomeRestrictionsArray ? 'Everyone' : 'Specific',
      customer_restrictions_filters: customer_restrictions
    });
  };

  const getDateString = (date: any, formatter: string) => {
    let reqDate = dayjs(date).format(formatter);
    return reqDate;
  };

  const getTimeString = (time: any, format: string) => {
    let reqTime = dayjs(time).format(format);
    return reqTime;
  };

  const loadEditBodyData = () => {
    const specicPOS =
      priceListCreateForm.getFieldsValue().specific_pos?.map((cv: any, icv: any) => {
        return { site_id: cv, site_name: cv };
      }) || [];

    const specificOnline =
      priceListCreateForm.getFieldsValue().specific_ecommerce?.map((cv: any, icv: any) => {
        return { site_id: cv, site_name: cv };
      }) || [];

    const parentListorMasterList =
      priceListCreateForm.getFieldsValue().parentListOrMasterList === 'list_parent' ? 'parentList' : 'master catalog';

    const isMasterValidate = priceListCreateForm.getFieldsValue().select_from_master_catalog_dropDown
      ? priceListCreateForm.getFieldsValue().select_from_master_catalog_dropDown
      : priceListCreateForm.getFieldsValue().select_from_master_catalog_TextBox;

    let customerRestrictionData = priceListCreateForm.getFieldsValue()?.customer_restrictions_filters;

    let customer_restriction_result = customerRestrictionData?.map((cv: any) => {
      if (cv.restriction_type === 'SEGMENT') {
        return { restriction_type: cv.restriction_type, restriction_value: cv.restriction_value };
      } else {
        return { restriction_type: cv.restriction_type, restriction_value: cv.restriction_value };
      }
    });

    let suggestionLine = priceListCreateForm.getFieldsValue().suggestion_list;
    let CatalogType =
      suggestionLine === 'list_other'
        ? 'IMPORT-CATALOG'
        : suggestionLine === 'list_parent'
        ? 'PARENT-LIST'
        : 'MASTER-CATALOG';
    const bodyData = {
      catalog_type: CatalogType,
      price_list_name: priceListCreateForm.getFieldsValue().name,
      name: priceListCreateForm.getFieldsValue().name,
      description: priceListCreateForm.getFieldsValue().description,
      status: priceListCreateForm.getFieldsValue().status,
      is_active: priceListCreateForm.getFieldsValue().is_active === 'active',
      rank: priceListCreateForm.getFieldsValue().rank,
      start_date: getDateString(priceListCreateForm.getFieldsValue().startDate, 'YYYY-MM-DD'),
      end_date: priceListCreateForm.getFieldsValue().endDate
        ? getDateString(priceListCreateForm.getFieldsValue().endDate, 'YYYY-MM-DD')
        : null,
      start_time: priceListCreateForm.getFieldsValue().startTime
        ? getTimeString(priceListCreateForm.getFieldsValue().startTime, 'HH:mm:ss')
        : '',
      end_time: priceListCreateForm.getFieldsValue().endTime
        ? getTimeString(priceListCreateForm.getFieldsValue().endTime, 'HH:mm:ss')
        : null,
      timezone: priceListCreateForm.getFieldsValue().time_Zone,
      parent_price_list_id: priceListCreateForm.getFieldsValue().parent_price_list
        ? priceListCreateForm.getFieldsValue().parent_price_list
        : null,
      master_catalog_id: priceListCreateForm.getFieldsValue().select_from_master_catalog_dropDown
        ? priceListCreateForm.getFieldsValue().select_from_master_catalog_dropDown
        : null,
      import_catalog_ref_name: priceListCreateForm.getFieldsValue().select_from_master_catalog_TextBox
        ? priceListCreateForm.getFieldsValue().select_from_master_catalog_TextBox
        : null,
      currency_code: priceListCreateForm.getFieldsValue().currency,
      is_pos:
        priceListCreateForm.getFieldsValue().isPriceListResolvable === 'priceListRes_yes'
          ? priceListCreateForm.getFieldsValue().POS
          : false,
      pos: specicPOS,
      is_online:
        priceListCreateForm.getFieldsValue().isPriceListResolvable === 'priceListRes_yes'
          ? priceListCreateForm.getFieldsValue().Ecommerce
          : false,
      online: specificOnline,
      customer_restrictions: priceListCreateForm.getFieldsValue().customer_restrictions_filters
        ? customer_restriction_result
        : null,
      is_resolvable: priceListCreateForm.getFieldsValue().isPriceListResolvable === 'priceListRes_yes',
      is_exclusive: priceListCreateForm.getFieldsValue().isPriceListExclusive === 'priceListExe_yes',
      rounding: {
        rounding_policy: priceListCreateForm.getFieldsValue().rounding_Policy,
        rounding_option:
          priceListCreateForm.getFieldsValue().rounding_Policy === 'NONE'
            ? priceListCreateForm.getFieldsValue().rounding_Option
            : null,
        rounding_amount:
          priceListCreateForm.getFieldsValue().rounding_Policy === 'NONE'
            ? String(priceListCreateForm.getFieldsValue().rounding_amount)
            : null
      }
    };
    return bodyData;
  };

  const handleOnSave = async () => {
    setLoading(true);
    const bodyData = loadEditBodyData();
    const { errors } = await priceListService.updatePriceListProduct(bodyData, parentId as any);
    if (_.isEmpty(errors)) {
      displaySuccessNotification({ message: 'Details Updated successfully!' });
      handleGoBack();
    } else {
      displayErrorNotifications(errors);
    }
    setLoading(false);
  };

  const handleGoBack = () => {
    navigate('/promotion-engine/promotions/price-list');
  };

  const handleOnClickDownload = async () => {
    setLoading(true);
    const { data, errors } = await priceListService.getDownloadData(parentId as string);
    if (_.isEmpty(errors)) {
      fileHelpers.triggerFileDownload({ data, fileName: 'price_enteries_download', extension: '.xlsx' });
      displaySuccessNotification({ message: 'Download success!' });
    } else {
      displayErrorNotifications(errors);
    }
    setLoading(false);
  };

  const onClickSuggestedLines = () => {
    const params = {
      offset: '0',
      backTabTo: 'PRICE-ENTERIES',
      isEditable: 'true'
    };
    const paramString = new URLSearchParams(params).toString();
    navigate(`/promotion-engine/promotions/price-list/${parentId}/price-list-edit-form/suggested-lines?${paramString}`);
  };

  const onClickCopyLines = () => {
    const params = {
      offset: '0',
      backTabTo: 'PRICE-ENTERIES',
      isEditable: 'true'
    };
    const paramString = new URLSearchParams(params).toString();
    navigate(`/promotion-engine/promotions/price-list/${parentId}/price-list-edit-form/copy-lines?${paramString}`);
  };

  const getProductName = () => {
    let responseData = basicDetailsResp ? basicDetailsResp : productData;
    let masterResp = masterCatalogOptions ? masterCatalogOptions : masterOptions; //array
    let parentResp = paretListDropDown ? paretListDropDown : parentOptions;
    let id = responseData?.master_catalog_id ? responseData?.master_catalog_id : responseData?.parent_price_list_id;
    let masterVal = masterResp.find((item: any) => item.value === id) || null;
    let parentVal = parentResp.find((item: any) => item.value === id) || null;
    let found_Id = responseData?.master_catalog_id ? masterVal : parentVal;
    let referedButton = responseData?.parent_price_list_id ? 'CopyLine' : 'Import';
    referedButton = responseData?.master_catalog_id ? 'SuggestedLine' : referedButton;
    let returnParams = {
      product_Name: found_Id ? found_Id.label : responseData?.import_catalog_ref_name || '',
      prefered_button: referedButton
    };
    return returnParams;
  };

  return (
    <PrimaryLayout>
      <div className="container mx-auto px-4">
        <Card>
          <Row>
            <Col>
              <Typography.Title level={3} className="text-[#2e2a5b]">
                Edit Price List
              </Typography.Title>
            </Col>
          </Row>
          <PriceListDiscountForm
            istabVisible={true}
            isformVisible={true}
            formVisiblityBasedOnStatus={formVisiblityBasedOnStatus}
            refUpdatBlock={refUpdatBlock}
            referenceBlockVisiblity={referenceBlockVisiblity}
            formInstance={priceListCreateForm}
            basicDetailsForm={priceListBasicDetailsForm}
            handleOnSave={handleOnSave}
            handleGoBack={handleGoBack}
            onClickSuggestedLines={onClickSuggestedLines}
            onClickCopyLines={onClickCopyLines}
            paretListDropDown={paretListDropDown}
            masterCatalogOptions={masterCatalogOptions}
            getProductName={getProductName}
            handleOnClickDownload={handleOnClickDownload}
            loadIntialData={onTabChange}
          />
        </Card>
      </div>
    </PrimaryLayout>
  );
};

export default PriceListEdit;
